import React, { useState, useEffect, useRef } from 'react';
import { 
  getCurrentDateForInput, 
  convertToDisplayFormat,
  getDatePlaceholder as getMomentDatePlaceholder,
  parseDateFromDisplay,
  formatDateForInput
} from '../../utils/momentDateFormatter';

interface DateInputProps {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  placeholder?: string;
  className?: string;
  label?: string;
  labelClassName?: string;
  inputClassName?: string;
  required?: boolean;
  disabled?: boolean;
}

/**
 * Custom Date Input component that uses moment.js for dd/MM/yyyy formatting
 * Supports both manual text entry and datepicker selection
 */
const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  min = getCurrentDateForInput(),
  placeholder = getMomentDatePlaceholder(),
  className = '',
  label,
  labelClassName,
  inputClassName,
  required = false,
  disabled = false
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  // Update display value when value changes
  useEffect(() => {
    if (value) {
      setDisplayValue(convertToDisplayFormat(value));
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Validate and format on blur
    if (displayValue) {
      const parsed = parseDateFromDisplay(displayValue);
      if (parsed) {
        const formatted = formatDateForInput(parsed);
        onChange(formatted);
      }
    }
  };

  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onChange(inputValue);
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;
    
    // Remove all non-digit characters
    const digitsOnly = inputValue.replace(/\D/g, '');
    
    // Format as dd/MM/yyyy
    let formatted = '';
    for (let i = 0; i < digitsOnly.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += digitsOnly[i];
    }
    
    setDisplayValue(formatted);
  };

  const handleCalendarClick = () => {
    if (dateInputRef.current && !disabled) {
      dateInputRef.current.focus();
      dateInputRef.current.showPicker?.();
    }
  };

  return (
    <div className="relative">
      {label && (
        <label className={labelClassName || "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        {/* Hidden date input for datepicker functionality */}
        <input
          ref={dateInputRef}
          type="date"
          value={value}
          onChange={handleDatePickerChange}
          min={min}
          required={required}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
          tabIndex={-1}
        />
        {/* Text input for manual entry */}
        <input
          ref={textInputRef}
          type="text"
          value={displayValue}
          onChange={handleTextInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={10}
          inputMode="numeric"
          className={inputClassName ? `${inputClassName} ${isFocused ? 'ring-2 ring-blue-600 border-transparent' : ''}` : `w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${className} ${isFocused ? 'ring-2 ring-blue-600 border-transparent' : ''}`}
        />
        {/* Calendar icon button */}
        <button
          type="button"
          onClick={handleCalendarClick}
          disabled={disabled}
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
          tabIndex={-1}
        >
          <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default DateInput;
